import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, filter, takeUntil } from 'rxjs';
import { DialogService } from '../../services/dialog.service';
import { DefaultValuesService } from '../../services/defaultvalues.service';
import { SaveService } from '../../services/save.service';
import { Utils } from '../../utils/utils';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { utils } from 'xlsx';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'fiscalyear',
    standalone: true,
    imports: [NgSelectModule, FormsModule],
    template: `
        Fiscal Year:<br />
        <ng-select
            [labelForId]="selectId"
            (change)="onSelect($event)"
            style="width:120px; border: 1px white solid" 
            class="top-main-select"
            [items]="list"
            bindLabel="label"
            bindValue="value"
            [closeOnSelect]="true"
            [searchable]="true"
            [(ngModel)]="defaults.fiscalYear">
        </ng-select>
    `
})
export class FiscalYearComponent implements OnInit, OnDestroy {
    private static nextId = 0;
    readonly selectId = `fiscalYearSelect${FiscalYearComponent.nextId++}`;
    list: any[] = [];
    value: number = 0;

    actualReportingLinks = [
        "/actualreporting",
        "/actualbranchreporting",
        "/pequarterreporting"
    ]

    mersyLinks = [
        "/actualreporting",
        "/actualbranchreporting",
        "/oplkpimanagement",
        "/oplkpibranchreporting",
        "/file/15", // Actual Reporting
        "/file/16", // Yearly OPL
        "/file/17", // Monthly OPL
        "/file/18", // Actual Branch Reporting
        "/file/19", // Yearly OPL Branch Reporting
        "/file/20",  // Monthly OPL Branch Reporting
        "/adminsection"
    ];

    branchExcellenceLinks = [
        "/focus-plan",
        "/assessment",
        "/pereporting",
        "/file/30",
        "/file/" + Utils.getFileUploadFocusPlanDefinition(),
    ];
    taxreportingLinks = [
        "/peyearlyplanning","/pequarterreporting"
    ];

    marketLinks = [
        "/segment-sheet",
        "/market-sheet",
        "/file/21",
        "/file/22"
    ];

    niOpsLinks = [
        "/niops",
        "/file/27"
    ];

    incoLinks = [
        "/inco-actualreporting",
        "/inco-oplreporting"
    ];

    previousMersyRel: boolean | null = null;
    previousIncoRel: boolean | null = null;
    previousMQspec: boolean | null = null;
    previousNIOps: boolean | null = null;
    previousBranchExcellenceRel: boolean | null = null;
    previousTaxReportingRel: boolean | null = null;
    previousActualReportingP1Rel: boolean | null = null;

    protected _onDestroy = new Subject<void>();

    private readonly now: Date;
    private readonly year: number;
    private readonly month: number;
    private readonly isPeriod1: boolean;
    
    constructor(
        private http: HttpClient,
        @Inject('BASE_URL') private baseUrl: string,
        public defaults: DefaultValuesService,
        private dialog: DialogService,
        public saveService: SaveService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.now = new Date();
        this.year = this.now.getFullYear();
        this.month = this.now.getMonth();
        this.isPeriod1 = this.month == 9;
    }

    async ngOnInit() {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._onDestroy)
        ).subscribe(async (event: any) => {
            await this.onInitAbstr(this.router.url);
        });

        // Only call directly if the router has already completed its initial navigation.
        // If not (e.g. hard refresh), the NavigationEnd subscription above will handle it.
        if (this.router.navigated) {
            await this.onInitAbstr(this.router.url);
        }
    }

    async onInitAbstr(url = "") {
        const isMersyRel = this.mersyLinks.includes(url);
        const isNiOpsRel = this.niOpsLinks.includes(url);
        const isBranchExcellenceRel = this.branchExcellenceLinks.includes(url);
        const isTaxReportingRel = this.taxreportingLinks.includes(url);
        const isMarketQueryRel = this.marketLinks.includes(url);
        const isActualReprtingP1Rel = this.isPeriod1 && this.actualReportingLinks.includes(url);
        const isIncoRel = this.incoLinks.includes(url);

        const changeYearsTrigger = this.shouldChangeYears(isMersyRel, isNiOpsRel, isBranchExcellenceRel, isTaxReportingRel, isMarketQueryRel, isActualReprtingP1Rel, isIncoRel);

        if (changeYearsTrigger) {
            this.dialog.tthis = this;

            await this.waitForInitialDataLoad();

            this.list = [];
            const localArrayList: any[] = this.generateYearList(isMersyRel, isMarketQueryRel, isNiOpsRel, isBranchExcellenceRel, isTaxReportingRel, isIncoRel);

            this.updateFiscalYear(localArrayList, isMersyRel, isMarketQueryRel, isNiOpsRel, isBranchExcellenceRel, isTaxReportingRel, isActualReprtingP1Rel, isIncoRel);

            this.previousMersyRel = isMersyRel;
            this.previousMQspec = isMarketQueryRel;
            this.previousNIOps = isNiOpsRel;
            this.previousBranchExcellenceRel = isBranchExcellenceRel;
            this.previousTaxReportingRel = isTaxReportingRel;
            this.previousActualReportingP1Rel = isActualReprtingP1Rel;
            this.previousIncoRel = isIncoRel;

            this.list = localArrayList;
        }
    }

    private shouldChangeYears(isMersyRel: boolean, isNiOpsRel: boolean, isBranchExcellenceRel: boolean, isTaxReportingRel: boolean, isMarquetQueryRel: boolean, isActualReprtingP1Rel: boolean, isIncoRel: boolean = false): boolean {
        return this.previousNIOps !== isNiOpsRel
            || this.previousBranchExcellenceRel !== isBranchExcellenceRel
            || this.previousActualReportingP1Rel !== isActualReprtingP1Rel
            || this.previousTaxReportingRel !== isTaxReportingRel
            || this.previousIncoRel !== isIncoRel
            || ((isMersyRel !== this.previousMersyRel && (this.month === 9 || this.month === 10))
                || (this.previousMQspec !== isMarquetQueryRel));
    }

    private async waitForInitialDataLoad() {
        while (!this.defaults.loadInitialData) {
            await new Promise(r => setTimeout(r, 1000));
        }
    }

    private generateYearList(isMersyRel: boolean, isMarketQueryRel: boolean, isNiOpsRel: boolean, isBranchExcellenceRel: boolean, isTaxReportingRel: boolean, isIncoRel: boolean = false): any[] {
        const localArrayList: any[] = [];

        if (isIncoRel) {
            const currentFiscalYear = this.month < 9 ? this.year : this.year + 1;
            
            if (this.defaults.isMersyAdminINCO) {
                for (let i = currentFiscalYear - 2; i <= currentFiscalYear + 1; i++) {
                    localArrayList.push({ 'label': `${i - 1}/${i}`, 'value': i });
                }
            } else if (this.month == 9 || this.month == 10) {
              localArrayList.push(
                { 'label': `${this.year - 1}/${this.year}`, 'value': this.year },
                { 'label': `${this.year}/${this.year + 1}`, 'value': this.year + 1 }
              );
            } else {
              localArrayList.push({ 'label': `${currentFiscalYear - 1}/${currentFiscalYear}`, 'value': currentFiscalYear });
            }
        } else if (isNiOpsRel) {
            if (this.month < 9) {
                localArrayList.push({ 'label': `${this.year - 1}/${this.year}`, 'value': this.year });
                localArrayList.push({ 'label': `${this.year}/${this.year + 1}`, 'value': this.year + 1 });
            } else {
                localArrayList.unshift({ 'label': `${this.year}/${this.year + 1}`, 'value': this.year + 1 });
                localArrayList.unshift({ 'label': `${this.year + 1}/${this.year + 2}`, 'value': this.year + 2 });
            }
        } else if (isBranchExcellenceRel || isTaxReportingRel) {
            if (this.month < 9) {
                localArrayList.push({ 'label': `${this.year - 1}/${this.year}`, 'value': this.year });
                localArrayList.push({ 'label': `${this.year}/${this.year + 1}`, 'value': this.year + 1 });
                localArrayList.push({ 'label': `${this.year + 1}/${this.year + 2}`, 'value': this.year + 2 });
                localArrayList.push({ 'label': `${this.year + 2}/${this.year + 3}`, 'value': this.year + 3 });
            } else if (
              (isBranchExcellenceRel && (this.month == 9 || this.month == 10)) || 
              (isTaxReportingRel && (this.month == 9 || this.month == 10 || this.month == 11))
            ) {
                localArrayList.unshift({ 'label': `${this.year - 1}/${this.year}`, 'value': this.year });
                localArrayList.unshift({ 'label': `${this.year}/${this.year + 1}`, 'value': this.year + 1 });
                localArrayList.unshift({ 'label': `${this.year + 1}/${this.year + 2}`, 'value': this.year + 2 });
                localArrayList.unshift({ 'label': `${this.year + 2}/${this.year + 3}`, 'value': this.year + 3 });
                localArrayList.unshift({ 'label': `${this.year + 3}/${this.year + 4}`, 'value': this.year + 4 });
            } 
            else {
                localArrayList.unshift({ 'label': `${this.year}/${this.year + 1}`, 'value': this.year + 1 });
                localArrayList.unshift({ 'label': `${this.year + 1}/${this.year + 2}`, 'value': this.year + 2 });
                localArrayList.unshift({ 'label': `${this.year + 2}/${this.year + 3}`, 'value': this.year + 3 });
                localArrayList.unshift({ 'label': `${this.year + 3}/${this.year + 4}`, 'value': this.year + 4 });
            }
        } else if (isMarketQueryRel) {
            for (let i = 2023; i <= this.year - 1; i++) {
                localArrayList.push({ 'label': `${i - 1}/${i}`, 'value': i });
            }
        } else if (this.defaults.isMersyAdminUser || this.defaults.isMersyOwnerUser) {
            for (let i = this.year - 5; i <= this.year + 3; i++) {
                localArrayList.push({ 'label': `${i - 1}/${i}`, 'value': i });
            }
        } else if (isMersyRel && this.month == 9 || this.month == 10) {
                localArrayList.unshift({ 'label': `${this.year - 1}/${this.year}`, 'value': this.year });
                localArrayList.unshift({ 'label': `${this.year}/${this.year + 1}`, 'value': this.year + 1 });
        } else {
            if (this.month < 9) {
                localArrayList.push({ 'label': `${this.year - 1}/${this.year}`, 'value': this.year });
            } else {
                localArrayList.unshift({ 'label': `${this.year}/${this.year + 1}`, 'value': this.year + 1 });
            }
        }
        return localArrayList;
    }

    private updateFiscalYear(fiscalYearList: any[], isMersyRel: boolean, isMarketQueryRel: boolean, isNiOpsRel: boolean, isBranchExcellenceRel: boolean, isTaxReportingRel: boolean, isActualReprtingP1Rel: boolean, isIncoRel: boolean = false) {
        const fiscalYearExists = fiscalYearList.some(year => year.value === this.value);
        if (isActualReprtingP1Rel) {
            this.value = this.year;
            this.changeFiscalYear();
        }
        else if (isIncoRel) {
            const currentFiscalYear = this.month < 11 ? this.year : this.year + 1;
            if (this.value === 0) {
                this.value = currentFiscalYear;
                this.defaults.fiscalYear = this.value;
            } else if (this.value !== currentFiscalYear) {
                this.value = currentFiscalYear;
                this.changeFiscalYear();
            }
        }
        else if (isNiOpsRel) {
            this.value = this.month < 9 ? this.year : this.year + 1;
            this.changeFiscalYear();
        }
        else if (isMarketQueryRel) {
            if (!fiscalYearExists) {
                this.value = +fiscalYearList[fiscalYearList.length - 1].value;
                this.changeFiscalYear();
            }
        } else if (isMersyRel) {
            if (this.month < 9) {
                this.value = this.year
            }
            else {
                this.value = this.year + 1;
            }
            this.changeFiscalYear();
        } else if (isBranchExcellenceRel || isTaxReportingRel) {
            this.value = this.month < 9 ? this.year : this.year + 1;
            this.changeFiscalYear();
        }
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    changeFiscalYear() {
        this.defaults.fiscalYear = this.value;
        this.sendPost();
    }

    onNo() {
        this.saveService.fireCancelAll();
        this.changeFiscalYear();
    }

    onSelect(event: any) {
        if (!event.value) return;
        this.value = +event.value;
        if (this.saveService.isSaveButtonShown()) {
            this.dialog.showConfirmDialog("Do you want to save the changes? If you select No, all changes will be lost!", "Save", this);
        } else {
            this.onNo();
        }
    }

    onYes() {
        this.saveService.fireSave();
        this.changeFiscalYear();
    }

    sendPost() {
        this.defaults.fiscalYear = this.value;
        this.http.post<string>(this.baseUrl + Utils.getFiscalYearAPI(), this.defaults.fiscalYear)
            .subscribe(() => {
                this.defaults.notifyFiscalYearChange();
            }, error => Utils.writeLog(this.http, this.baseUrl, error));
    }
}